import { desktopBridge } from './desktop-bridge';
import { desktopCapabilities } from './desktop-capabilities';
import { desktopPrintingApi } from './desktop-printing.api';
import {
  DesktopDocumentType,
  DesktopPrintDocument,
  DesktopPrintingCapabilities,
  PrintRequest,
  PrintResult,
  PrintTransport,
} from './printing.types';

export interface DesktopTransportDependencies {
  getBridge: typeof desktopBridge.get;
  getCapabilities: () => Promise<DesktopPrintingCapabilities>;
  getDocument: (
    type: DesktopDocumentType,
    id: number
  ) => Promise<DesktopPrintDocument>;
  invalidateCapabilities: () => void;
}

const defaultDependencies: DesktopTransportDependencies = {
  getBridge: desktopBridge.get,
  getCapabilities: () => desktopCapabilities.get(),
  getDocument: desktopPrintingApi.getDocument,
  invalidateCapabilities: desktopCapabilities.invalidate,
};

export class DesktopPrintTransport implements PrintTransport {
  constructor(private readonly dependencies = defaultDependencies) {}

  async isAvailable(documentType: DesktopDocumentType): Promise<boolean> {
    const bridge = this.dependencies.getBridge();
    if (!bridge) return false;

    try {
      const status = await bridge.status();
      if (!status.available) return false;

      const capabilities = await this.dependencies.getCapabilities();
      return (
        capabilities.enabled &&
        capabilities.formats.includes('ESC_POS') &&
        capabilities.supportedDocumentTypes.includes(documentType)
      );
    } catch {
      return false;
    }
  }

  async print(request: PrintRequest): Promise<PrintResult> {
    const bridge = this.dependencies.getBridge();
    if (!bridge) {
      throw Object.assign(new Error('No se detectó Codenova POS Desktop.'), {
        code: 'BRIDGE_ERROR',
      });
    }

    try {
      const document = await this.dependencies.getDocument(
        request.documentType,
        request.documentId
      );

      if (document.format !== 'ESC_POS') {
        throw Object.assign(new Error('El formato Desktop no es ESC_POS.'), {
          code: 'INVALID_FORMAT',
        });
      }
      if (!document.contentBase64?.trim()) {
        throw Object.assign(new Error('La respuesta no contiene contentBase64.'), {
          code: 'INVALID_BASE64',
        });
      }

      const result = await bridge.printRaw({
        jobId: document.jobId,
        printerRole: document.printerRole,
        contentBase64: document.contentBase64,
        copies: document.copies ?? 1,
      });

      return {
        transport: 'desktop',
        jobId: result.jobId,
        printerRole: document.printerRole,
        printerName: result.printerName,
        copies: result.copies,
        bytes: result.bytes,
        duplicate: result.duplicate,
      };
    } catch (error) {
      this.dependencies.invalidateCapabilities();
      throw error;
    }
  }
}
